import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Block as BlockIcon } from '@mui/icons-material';
import { EVOLUTION_THEME, evolutionCancelButtonSx } from './evolutionTheme';
import {
  EVOLUTION_INVALIDATION_MAX_REASON,
  EVOLUTION_INVALIDATION_MIN_REASON,
} from '../../utils/evolutionRecordPolicy';

interface EvolutionInvalidateDialogProps {
  open: boolean;
  /** Identificação curta do registro (ex.: "12/03/2026 — Dra. Ana"). */
  subtitle?: string;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const EvolutionInvalidateDialog: React.FC<EvolutionInvalidateDialogProps> = ({
  open,
  subtitle,
  submitting = false,
  error,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setTouched(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < EVOLUTION_INVALIDATION_MIN_REASON;
  const showError = touched && tooShort;

  const handleConfirm = () => {
    setTouched(true);
    if (tooShort || submitting) return;
    onConfirm(trimmed);
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BlockIcon sx={{ color: EVOLUTION_THEME.cancelledText }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: EVOLUTION_THEME.navy, lineHeight: 1.2 }}>
              Invalidar evolução
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: EVOLUTION_THEME.slate }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          A evolução continua no prontuário como registro histórico, marcada como invalidada, e não
          poderá mais ser editada. A justificativa fica visível junto ao registro.
        </Alert>

        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          maxRows={10}
          label="Justificativa da invalidação"
          placeholder="Descreva o motivo (ex.: registro lançado no paciente errado)."
          value={reason}
          disabled={submitting}
          onChange={(e) => setReason(e.target.value.slice(0, EVOLUTION_INVALIDATION_MAX_REASON))}
          onBlur={() => setTouched(true)}
          error={showError}
          helperText={
            showError
              ? `Descreva o motivo com pelo menos ${EVOLUTION_INVALIDATION_MIN_REASON} caracteres.`
              : `${trimmed.length}/${EVOLUTION_INVALIDATION_MAX_REASON} caracteres`
          }
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={evolutionCancelButtonSx}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || tooShort}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: EVOLUTION_THEME.cancelledText,
            '&:hover': { bgcolor: EVOLUTION_THEME.cancelledTextDark },
          }}
        >
          Invalidar evolução
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EvolutionInvalidateDialog;
